"use client";

import "@/app/templates/data/dw-amora/style.css";
import { caudex } from "@/app/templates/data/dw-amora/fonts";
import Image from "next/image";
import { useMemo, useState, useRef, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, Video, MessageCircleHeart } from "lucide-react";
import { schemaDummy } from "@/app/templates/data/dw-amora/schemaDummy";
import { componentsMap } from "@/app/templates/data/dw-amora/componentsMap";
import Footer from "@/app/templates/data/dw-amora/components/Footer";
import OpenInvitation from "@/app/templates/data/dw-amora/components/OpenInvitation";
import { getDiy } from "@/lib/diy";
import { getDiyEditorContext } from "@/context/diyEditorContext";
import { getMediaUrl } from "@/lib/upload";
import LoadingScreen from "@/components/ui/LoadingScreen";

const stripDeadBlobs = (obj) => {
    if (!obj) return obj;
    if (typeof obj === 'string') {
        if (obj.startsWith('blob:')) return '';
        
        const tokenMatch = obj.match(/\/media\/([^/?#]+)/);
        const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "/api/backend";
        if (tokenMatch && !obj.includes(baseUrl.replace(/\/$/, ""))) {
            return getMediaUrl(tokenMatch[1]);
        }
        
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(stripDeadBlobs);
    }
    if (typeof obj === 'object') {
        const newObj = {};
        for (const key in obj) {
            newObj[key] = stripDeadBlobs(obj[key]);
        }
        return newObj;
    }
    return obj;
};

const toRenderablePayload = (rawPayload) => {
    const fallbackPayload = stripDeadBlobs(schemaDummy) || {};
    if (!rawPayload || typeof rawPayload !== "object") {
        return fallbackPayload;
    }

    const cleanedPayload = stripDeadBlobs(rawPayload) || {};
    return {
        ...fallbackPayload,
        ...cleanedPayload,
        templateMeta: {
            ...(fallbackPayload.templateMeta || {}),
            ...(cleanedPayload.templateMeta || {}),
        },
        sectionSchemas: {
            ...(fallbackPayload.sectionSchemas || {}),
            ...(cleanedPayload.sectionSchemas || {}),
        },
        desktopCoverConfig: {
            ...(fallbackPayload.desktopCoverConfig || {}),
            ...(cleanedPayload.desktopCoverConfig || {}),
            props: {
                ...(fallbackPayload.desktopCoverConfig?.props || {}),
                ...(cleanedPayload.desktopCoverConfig?.props || {}),
            },
        },
        openInvitationConfig: {
            ...(fallbackPayload.openInvitationConfig || {}),
            ...(cleanedPayload.openInvitationConfig || {}),
            props: {
                ...(fallbackPayload.openInvitationConfig?.props || {}),
                ...(cleanedPayload.openInvitationConfig?.props || {}),
            },
        },
        defaultSections: Array.isArray(cleanedPayload.defaultSections)
            ? cleanedPayload.defaultSections
            : Array.isArray(fallbackPayload.defaultSections)
                ? fallbackPayload.defaultSections
                : [],
    };
};

export default function Page({ initialPayload = null } = {}) {
    const [isOpened, setIsOpened] = useState(false);
    const videoRef = useRef(null);
    
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);
    const pathname = usePathname();
    const isPreviewMode = typeof pathname === "string" && pathname.includes("/preview");
    
    const editorContext = getDiyEditorContext("dw-amora");
    const orderId = editorContext?.orderId || null;

    const [savedPreviewData, setSavedPreviewData] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    
    const messages = ["Buat Ucapan", "Buka Virtual Booth"];
    const [currentIndex, setCurrentIndex] = useState(0);
    const fallbackPayload = useMemo(() => toRenderablePayload(schemaDummy), []);
    const normalizedInitialPayload = useMemo(() => {
        if (!initialPayload || typeof initialPayload !== "object") {
            return null;
        }
        return toRenderablePayload(initialPayload);
    }, [initialPayload]);

    useEffect(() => {
        let isCancelled = false;

        const loadPreviewData = async () => {
            try {
                let payload = normalizedInitialPayload || fallbackPayload;
                if (isPreviewMode && !normalizedInitialPayload) {
                    try {
                        const res = await getDiy("dw-amora", { orderId: orderId || undefined });
                        if (res?.status?.code === 200 && res?.data?.payload) {
                            payload = toRenderablePayload(res.data.payload);
                        }
                    } catch (fetchErr) {
                        console.warn("No active draft found or unauthorized, falling back to default dummy schema.");
                    }
                }

                if (isPreviewMode && !isCancelled) {
                    setSavedPreviewData(payload);
                } else if (!isPreviewMode && !isCancelled) {
                    setSavedPreviewData(null);
                }

                if (!payload || typeof payload !== "object") {
                    if (!isCancelled) {
                        setLoadingProgress(100);
                        setInitialLoading(false);
                    }
                    return;
                }

                const urls = [];
                const extract = (obj) => {
                    if (typeof obj === 'string') {
                        const isUrl = obj.startsWith('http') || obj.startsWith('/');
                        const hasExtension = obj.match(/\.(jpg|jpeg|png|gif|webp|mp4|webm|mp3|wav|dwm|dwj)/i);
                        const isMediaToken = obj.match(/\/media\/[^/?#]+/);
                        if (isUrl && (hasExtension || isMediaToken)) {
                            urls.push(obj);
                        }
                    } else if (Array.isArray(obj)) {
                        obj.forEach(extract);
                    } else if (typeof obj === 'object' && obj !== null) {
                        Object.values(obj).forEach(extract);
                    }
                }
                
                if (payload.templateMeta) extract(payload.templateMeta);
                if (payload.desktopCoverConfig) extract(payload.desktopCoverConfig);
                if (payload.openInvitationConfig) extract(payload.openInvitationConfig);
                if (payload.defaultSections) extract(payload.defaultSections);
                
                const uniqueUrls = [...new Set(urls)];
                if (uniqueUrls.length === 0) {
                    if (!isCancelled) {
                        setLoadingProgress(100);
                        setInitialLoading(false);
                    }
                    return;
                }
                let loaded = 0;
                
                const promises = uniqueUrls.map(url => {
                    return new Promise((resolve) => {
                        const complete = () => {
                            if (isCancelled) return;
                            loaded++;
                            setLoadingProgress(Math.round((loaded / uniqueUrls.length) * 100));
                            resolve();
                        };
                        if (url.match(/\.(mp4|webm|dwj)$/i)) {
                            const v = document.createElement('video');
                            v.onloadeddata = complete; v.onerror = complete; v.src = url; v.load();
                        } else if (url.match(/\.(mp3|wav|dwm)$/i)) {
                            const a = new Audio();
                            a.onloadeddata = complete; a.onerror = complete; a.src = url; a.load();
                        } else {
                            const i = new window.Image();
                            i.onload = complete; i.onerror = complete; i.src = url;
                        }
                    });
                });
                
                await Promise.race([
                    Promise.all(promises),
                    new Promise(r => setTimeout(r, 15000))
                ]);

                if (!isCancelled) {
                    setInitialLoading(false);
                }
            } catch (err) {
                console.warn("Failed to load preview data:", err);
                if (!isCancelled) setInitialLoading(false);
            }
        };

        loadPreviewData();
        return () => {
            isCancelled = true;
        };
    }, [fallbackPayload, isPreviewMode, normalizedInitialPayload, orderId]);

    const data = useMemo(() => {
        if (isPreviewMode) {
            return savedPreviewData || fallbackPayload;
        }
        if (normalizedInitialPayload) {
            return normalizedInitialPayload;
        }
        return fallbackPayload;
    }, [fallbackPayload, isPreviewMode, normalizedInitialPayload, savedPreviewData]);

    const templateMeta = data?.templateMeta || {};
    const desktopCoverProps = data?.desktopCoverConfig?.props || {};
    const openInvitationProps = data?.openInvitationConfig?.props || {};
    const renderedSections = Array.isArray(data?.defaultSections)
        ? data.defaultSections
        : [];
    const mediaConfig = templateMeta?.media || {};
    const videoBackground =
        mediaConfig?.videoBackground ?? templateMeta?.videoBackground ?? "";
    const audioBackground =
        mediaConfig?.audioBackground ?? templateMeta?.audioBackground ?? "";
    const hasAudio = typeof audioBackground === "string" && audioBackground.trim() !== "";

    useEffect(() => {
        const currentAudio = audioRef.current;
        return () => {
            if (currentAudio) {
                currentAudio.pause();
            }
        };
    }, []);

    const handleOpen = () => {
        setIsOpened(true);
        const playPromise = videoRef.current?.play();
        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                if (error.name !== "AbortError") {
                    console.warn("Autoplay failed:", error);
                }
            });
        }

        if (hasAudio && audioRef.current && typeof audioRef.current.play === "function") {
            document.querySelectorAll('audio').forEach(a => {
                if (a !== audioRef.current) a.pause();
            });
            audioRef.current
                .play()
                .catch((err) => {
                    if (err.name !== "AbortError") {
                        console.warn("Audio autoplay failed:", err);
                    }
                });
        }
    };

    const toggleAudio = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            document.querySelectorAll('audio').forEach(a => {
                if (a !== audio) a.pause();
            });
            audio
                .play()
                .catch(() => { });
        } else {
            audio.pause();
        }
    };

    if (initialLoading) {
        return <LoadingScreen progress={loadingProgress} />;
    }

    // Floating button logic
    const currentMessage = messages[currentIndex];
    const isUcapan = currentMessage === "Buat Ucapan";
    const currentIcon = isUcapan ? <MessageCircleHeart /> : <Video />;

    const toVirtualBooth = () => {
        const boothElement = document.getElementById("virtualbooth");
        if (boothElement) {
            boothElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div className={`bg-white ${caudex.variable} antialiased lg:h-screen w-full`}>
            <div className="block lg:flex">
                {/* Panel kiri */}
                <div className="hidden lg:block bg-black/90 lg:w-[65%] lg:h-screen lg:relative">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute z-20 left-15 bottom-20"
                    >
                        <h1 className="text-white uppercase font-cormorant text-4xl leading-relaxed tracking-widest drop-shadow-lg">
                            {`${templateMeta?.groomFirstName || "Groom"} & ${templateMeta?.brideFirstName || "Bride"}`}
                        </h1>
                        <p className="text-white font-sans font-caudex text-xs tracking-widest drop-shadow-lg">
                            {desktopCoverProps.subtitle}
                        </p>
                    </motion.div>
                    <Image
                        className="object-cover"
                        src={
                            desktopCoverProps.backgroundImage
                            || schemaDummy?.desktopCoverConfig?.props?.backgroundImage
                        }
                        alt="cover"
                        fill
                        priority
                    />
                </div>

                {/* Panel kanan */}
                <div className="relative w-full lg:w-[35%] lg:h-screen overflow-hidden">
                    {/* Floating Button */}
                    <motion.div
                        onClick={toVirtualBooth}
                        key={currentIndex}
                        initial={{ opacity: 0.1 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                        className="cursor-pointer bg-white/25 border border-white hover:bg-white/35 duration-200 text-white px-4 py-1.5 rounded-full flex items-center space-x-2 fixed bottom-0 right-0 lg:right-5 m-5 z-50"
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                transition={{ duration: 0.5 }}
                                className="flex items-center space-x-2"
                            >
                                <motion.div
                                    key={isUcapan ? "ucapan-icon" : "booth-icon"}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {currentIcon}
                                </motion.div>
                                <p className="whitespace-nowrap">{currentMessage}</p>
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>

                    {/* Open Invitation overlay */}
                    {!isOpened && (
                        <div className="absolute inset-0 z-50">
                            <OpenInvitation
                                props={openInvitationProps}
                                templateMeta={templateMeta}
                                onOpen={handleOpen}
                                isCustomize={false}
                                __isMobile={true}
                            />
                        </div>
                    )}

                    {/* Video background */}
                    <div className="absolute inset-0 z-0">
                        <video
                            ref={videoRef}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                            preload="auto"
                            aria-hidden="true"
                        >
                            <source src={videoBackground} />
                            <track kind="captions" label="No captions available" />
                        </video>
                        <div className="absolute inset-0 bg-black/50" />
                    </div>

                    {/* Content */}
                    <div className="relative z-10 w-full overflow-y-auto h-screen">
                        {renderedSections
                            .filter((section) => section.enabled)
                            .sort((a, b) => a.order - b.order)
                            .map((section) => {
                                const type = typeof section.type === "string" ? section.type.toLowerCase() : section.type;
                                const Component = componentsMap[type];
                                if (!Component) return null;
                                return (
                                    <Component 
                                        key={section.id} 
                                        {...section.props} 
                                        templateMeta={templateMeta}
                                    __isMobile={true}
                                        {...(type === "hero" ? {
                                            marriageContractDate: templateMeta?.marriageContractDate,
                                            receptionDate: templateMeta?.receptionDate,
                                        } : {})}
                                        {...(["events", "rsvp", "countdown"].includes(type) ? {
                                            marriageContractDate: templateMeta?.marriageContractDate,
                                            receptionDate: templateMeta?.receptionDate,
                                            countdownSource: templateMeta?.countdownSource,
                                            countdownDate: templateMeta?.countdownSource === "reception" 
                                                ? templateMeta?.receptionDate 
                                                : templateMeta?.marriageContractDate
                                        } : {})}
                                    />
                                );
                            })}
                        <Footer />
                    </div>

                    {/* AUDIO */}
                    {hasAudio && (
                        <audio
                            ref={audioRef}
                            loop
                            preload="auto"
                            src={audioBackground}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                        />
                    )}

                    {/* TOGGLE AUDIO BUTTON */}
                    {hasAudio && (
                        <button
                            onClick={toggleAudio}
                            className="fixed bottom-5 left-5 z-50 bg-white text-black p-2 border border-gray-200 rounded-full shadow-lg hover:bg-black hover:text-white transition-colors cursor-pointer"
                            aria-label="Toggle Music"
                        >
                            {isPlaying ? <Volume2 size={20} /> : <VolumeX size={20} />}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
